import React from "react";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";

import SwipeDeckPlayer from "../main/content/take_training_employee/swipe_deck_player/SwipeDeckPlayer";
import {
  API_get_public_swipe_deck,
  API_post_public_swipe_deck_opened,
} from "../constants";

/**
 * Public, unauthenticated swipe-deck page (/s/<token>).
 *
 * Renders a single shared deck for anyone with the link — no login, no app
 * shell, and NO tracking. Data is fetched with bare axios (no auth token) from
 * the AllowAny public endpoint, and the deck is handed to the existing
 * SwipeDeckPlayer in `publicShareToken` mode (polls tally anonymously, quizzes
 * grade server-side, nothing per-user is written).
 */
function PublicSwipeDeckView({ token }) {
  const [deck, setDeck] = React.useState(null);
  const [status, setStatus] = React.useState("loading"); // loading | ready | not_found | error

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_get_public_swipe_deck}${token}`);
        if (cancelled) return;
        const payload = res?.data?.swipe_deck;
        if (!payload) {
          setStatus("not_found");
          return;
        }
        setDeck(payload);
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        setStatus(err?.response?.status === 404 ? "not_found" : "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Best-effort anonymous reach: bump the open counter once per browser.
  React.useEffect(() => {
    if (status !== "ready" || !token) return;
    const key = `public_deck_opened_${token}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch (e) {
      // localStorage unavailable (private mode) — still fire once per load.
    }
    axios
      .post(API_post_public_swipe_deck_opened, { share_token: token })
      .catch(() => {});
  }, [status, token]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <CircularProgress sx={{ color: "#ffffff" }} />
      </div>
    );
  }

  if (status === "not_found" || status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 px-6">
        <div className="text-center max-w-sm">
          <h1 className="text-xl font-semibold text-white mb-2">
            {status === "not_found"
              ? "This link isn't available"
              : "Something went wrong"}
          </h1>
          <p className="text-sm text-slate-300">
            {status === "not_found"
              ? "This share link may have been turned off or no longer exists."
              : "Please try again in a moment."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <SwipeDeckPlayer
      previewDeck={deck}
      publicShareToken={token}
      onClose={() => {}}
      onCompleted={() => {}}
    />
  );
}

export default PublicSwipeDeckView;
